import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';

import GET_USER_ORGANIZATIONS, {
  GetUserOrganizations,
  GetUserOrganizationsVariables,
} from '../../graphql/queries/get-user-organizations';
import { useAppAuth } from '../../hooks/use-app-auth';
import { Organization } from '../../graphql/types/organizations';

interface OrganizationSwitchModalProps {
  open: boolean;
  onClose: () => void;
  currentOrgId?: string;
}

const PAGE_SIZE = 10;

const OrganizationSwitchModal: React.FC<OrganizationSwitchModalProps> = ({
  open,
  onClose,
  currentOrgId = undefined,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'organization-switch-modal' });
  const [ currentPage, setCurrentPage ] = useState(1);
  const [ selectedOrgId, setSelectedOrgId ] = useState<string | null>(null);
  const [ switching, setSwitching ] = useState(false);
  const [ searchTerm, setSearchTerm ] = useState('');
  const auth = useAppAuth();

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPage(1);
    setSearchTerm(e.target.value);
  };

  const {
    data, loading, error, refetch,
  } = useQuery<GetUserOrganizations, GetUserOrganizationsVariables>(
    GET_USER_ORGANIZATIONS,
    {
      skip: !open,
      fetchPolicy: 'cache-and-network',
    },
  );

  // Filter and sort organizations based on search term
  const filteredOrganizations = React.useMemo(() => {
    const allOrganizations = data?.getUserOrganizations || [];

    // Filter based on search term
    const filtered = searchTerm.trim()
      ? allOrganizations.filter((org) =>
        (org.display_name || org.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      : allOrganizations;

    // Sort alphabetically by display name, but put current org first
    return [ ...filtered ].sort((a, b) => {
      // Current org always comes first
      if (a.id === currentOrgId) return -1;
      if (b.id === currentOrgId) return 1;

      // Sort others alphabetically by display name
      const nameA = (a.display_name || a.name).toLowerCase();
      const nameB = (b.display_name || b.name).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [
    data?.getUserOrganizations,
    searchTerm,
    currentOrgId,
  ]);

  const totalCount = filteredOrganizations.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Calculate pagination
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const organizations = filteredOrganizations.slice(startIndex, endIndex);

  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleOrganizationSelect = (orgId: string) => {
    setSelectedOrgId(orgId);
  };

  const handleSwitchConfirm = async () => {
    if (selectedOrgId && selectedOrgId !== currentOrgId) {
      setSwitching(true);
      try {
        const redirectUri = window.location.origin;

        await auth.loginWithRedirect({
          appState: {
            returnTo: redirectUri + '/#/',
          },
          authorizationParams: {
            organization: selectedOrgId,
            prompt: 'none', // Don't show login UI
            redirect_uri: redirectUri,
          },
        });
      } catch (error) {
        console.error('Organization switch failed:', error);
        setSwitching(false);
      }
    }
  };

  // Reset to first page when modal opens
  React.useEffect(() => {
    if (open) {
      setCurrentPage(1);
      setSelectedOrgId(null);
      setSearchTerm('');
    }
  }, [ open ]);

  const handleRetry = () => {
    refetch();
  };

  if (error) {
    return (
      <Dialog
        fullWidth
        maxWidth='sm'
        open={open}
        onClose={onClose}
      >
        <DialogTitle>
          <Box
            alignItems='center'
            display='flex'
            justifyContent='space-between'
          >
            <Typography variant='h6'>Switch Organization</Typography>
            <IconButton
              size='small'
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            py={3}
            textAlign='center'
          >
            <Typography
              gutterBottom
              color='error'
            >
              {t('failed_to_load_organizations')}
            </Typography>
            <Typography
              gutterBottom
              color='textSecondary'
              variant='body2'
            >
              {error.message}
            </Typography>
            <Button
              sx={{ mt: 2 }}
              variant='outlined'
              onClick={handleRetry}
            >
              {t('retry')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        <Box
          alignItems='center'
          display='flex'
          justifyContent='space-between'
        >
          <Typography variant='h6'>{t('switch_organization')}</Typography>
          <IconButton
            size='small'
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && organizations.length === 0
          ? (
            <Box
              display='flex'
              justifyContent='center'
              py={4}
            >
              <CircularProgress />
            </Box>
            )
          : (
            <>
              <TextField
                fullWidth
                placeholder={t('search_organizations')}
                size='small'
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ mb: 2 }}
                value={searchTerm}
                variant='outlined'
                onChange={handleSearchTermChange}
              />
              <List dense>
                {organizations.map((org: Organization) => (
                  <ListItem
                    key={org.id}
                    disablePadding
                  >
                    <ListItemButton
                      disabled={org.id === currentOrgId}
                      selected={selectedOrgId === org.id}
                      sx={{
                        py: 0.5,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: 'transparent',
                        '&.Mui-selected': {
                          borderColor: 'primary.main',
                          '&:hover': {
                            borderColor: 'primary.dark',
                          },
                        },
                      }}
                      onClick={() => handleOrganizationSelect(org.id)}
                    >
                      <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Avatar
                          alt={org.display_name || org.name}
                          src={org.branding?.logo_url}
                          sx={{
                            width: 28,
                            height: 28,
                          }}
                        >
                          {(org.display_name || org.name).charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${org.display_name || org.name}${org.id === currentOrgId ? ` (${t('current_organization')})` : ''}`}
                        secondary={org.id}
                        slotProps={{
                          primary: { variant: 'body2' },
                          secondary: { variant: 'caption' },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {organizations.length === 0 && !loading && (
                <Box
                  py={2}
                  textAlign='center'
                >
                  <Typography
                    color='textSecondary'
                    variant='body2'
                  >
                    {searchTerm ? t('no_organizations_match_search') : t('no_organizations_available')}
                  </Typography>
                </Box>
              )}

              {totalCount > PAGE_SIZE && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    alignItems='center'
                    display='flex'
                    justifyContent='space-between'
                  >
                    <Button
                      disabled={!hasPreviousPage || loading}
                      size='small'
                      startIcon={<ChevronLeftIcon />}
                      onClick={handlePreviousPage}
                    >
                      {t('previous')}
                    </Button>
                    <Typography
                      color='textSecondary'
                      variant='body2'
                    >
                      {t('page_of', {
                        currentPage,
                        totalPages,
                      })}
                    </Typography>
                    <Button
                      disabled={!hasNextPage || loading}
                      endIcon={<ChevronRightIcon />}
                      size='small'
                      onClick={handleNextPage}
                    >
                      {t('next')}
                    </Button>
                  </Box>
                </>
              )}
            </>
            )}
      </DialogContent>
      <DialogActions>
        <Button
          disabled={switching}
          onClick={onClose}
        >
          {t('cancel')}
        </Button>
        <Button
          disabled={!selectedOrgId || selectedOrgId === currentOrgId || switching}
          variant='contained'
          onClick={handleSwitchConfirm}
        >
          {switching ? <CircularProgress size={20} /> : t('switch_organization_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationSwitchModal;
